import { meanUserId } from "@lib/constants";
import {
  mvfProfileFromTrancheProfile,
  tranchesFromMVFProfile,
  type MVF,
  type Tranche,
} from "@lib/tranche";
import type { Evaluator, Track } from "@lib/types";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { validatePoints } from "@lib/validatePoints";
import { cloneDeep } from "lodash";
import includes from "lodash/includes";
import isArray from "lodash/isArray";
import isEqualWith from "lodash/isEqualWith";
import isObject from "lodash/isObject";
import omitBy from "lodash/omitBy";
import { create, rawReturn } from "mutative";
import ChainedError from "typescript-chained-error";
import { z } from "zod";
import {
  assertGetEvaluable,
  assertGetEvaluator,
  assertGetField,
  assertGetFunder,
  assertGetMember,
  assertGetProposal,
  assertGetProposalForm,
  assertGetRecommender,
  assertShortnameIsUnique,
  evaluatorIsMeanRecommender,
  getEvaluation,
  getEvaluator,
  getMemberForEvaluator,
  getProposal,
  getRecusalForEvaluator,
  getRoundMemberForEvaluator,
} from "./round/accessors";
import { action, type Action, type AddRound } from "./round/actions";
import type {
  Disclosure,
  Evaluation,
  RoundAggregate,
  RoundMember,
  TypedFields,
} from "./round/aggregate";
import {
  getFunderBudgetFromIncrementalEvalSteps,
  trancheListFromIncrementalEvalSteps,
} from "./round/incrementalEvalsUtils";
import {
  initEvaluator,
  initFunder,
  initRecommender,
} from "./round/initEvaluator";
import { normalizeTrancheList } from "./round/normalizeTrancheList";
import { isActionPermitted } from "./round/permissions";

export interface Aggregate {
  type: string;
  version: number;
  id: string;
}

export type ActionType = Action["type"];

export const actionMessage = z.object({
  aggregateId: z.string(),
  payload: action,
  // 99% of the time, underlyingUserId and actingAsUserId are the same.
  // They're only different when an admin acts as a user.
  underlyingUserId: z.string(),
  actingAsUserId: z.string(),
  id: z.string(),
  version: z.number(),
  timestamp: z.string(),

  // undoes is set as metadata for both undos and redos.
  // For a redo it's the undo action that we're undoing.
  undoes: z.string().nullable(),
  // redoes is set as metadata for only redos (not undos).
  // It's the action that the undo that we're undoing undid 🤔
  redoes: z.string().nullable(),
});

export const unconfirmedActionMessage = actionMessage.omit({
  version: true,
  timestamp: true,
});

export type ActionMessage = z.infer<typeof actionMessage>;
export type UnconfirmedActionMessage = z.infer<typeof unconfirmedActionMessage>;

const EPSILON = 0.0000001;

// Treat missing and undefined properties as equal
// https://stackoverflow.com/a/57894104/24041125
function comparisonFunc(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean | undefined {
  if (isArray(a) || isArray(b)) return;
  if (!isObject(a) || !isObject(b)) {
    // Handle number comparison with tolerance for floating point precision
    if (typeof a === "number" && typeof b === "number") {
      return Math.abs(a - b) < EPSILON;
    }
    return;
  }

  if (!includes(a, undefined) && !includes(b, undefined)) return;

  return isEqualWith(
    omitBy(a, (value) => value === undefined),
    omitBy(b, (value) => value === undefined),
    comparisonFunc,
  );
}

// Structural equality, but treating missing and undefined properties as equal
export function actionIsIdentical(
  action: Action,
  otherAction: Action,
): boolean {
  return isEqualWith(action, otherAction, comparisonFunc);
}

function applyAction(
  round: RoundAggregate,
  actionMessage: ActionMessage,
): RoundAggregate {
  const { timestamp, payload: action } = actionMessage;
  return create(round, (draft) => {
    switch (action.type) {
      case "CREATE_ROUND":
        return rawReturn(initializeRoundAggregate(actionMessage, action));
      case "ADD_ROUND_MEMBER": {
        const { userId, initials, track } = action;

        if (draft.members[userId]) {
          // If this user is already present in the round, either restore them
          // (if they were removed from the round), or throw an error (if they
          // were not previously removed and are being double-added).
          const existingMember = draft.members[userId];

          if (existingMember.status === "removed") {
            draft.members[userId] = {
              ...existingMember,
              status: "active",
            };
          } else {
            throw new Error(
              `User with id ${userId} already exists: ${existingMember.initials}`,
            );
          }
        } else {
          draft.members[userId] = {
            initials,
            createdAt: timestamp,
            status: "active" as const,
            userId,
            id: userId,
            isRoundAdmin: false,
            track,
            feedbackVisibleByDefault: true,
            evaluableFeedback: {},
            evaluableNotes: {},
            evaluablePermissions: {},
            evaluators: {},
          };
        }
        break;
      }
      case "ADD_PROPOSAL_FORM": {
        const {
          name,
          proposalFormId,
          googleFormId,
          spreadsheetId,
          spreadsheetRange,
        } = action;

        if (draft.proposalForms[proposalFormId]) {
          console.warn(
            `ADD_PROPOSAL_FORM: Proposal form with id ${proposalFormId} already exists`,
          );
          break;
        }

        draft.proposalForms[proposalFormId] = {
          id: proposalFormId,
          status: "active",
          fields: [],
          name,
          proposals: {},
          googleFormId,
          spreadsheetId,
          spreadsheetRange,
        };
        break;
      }
      case "ADD_OR_UPDATE_PROPOSAL_FORM_FIELD": {
        const { field, proposalFormId } = action;

        const proposalForm = assertGetProposalForm(draft, proposalFormId);
        proposalForm.fields.push(field);
        break;
      }
      case "ADD_EVALUATOR": {
        const {
          memberId: userId,
          shortname,
          evaluatorType,
          evaluatorId,
          threshold,
        } = action;

        assertShortnameIsUnique(draft, shortname);
        const evaluator = initEvaluator(
          evaluatorId,
          userId,
          shortname,
          evaluatorType,
          draft.holdProposalId,
        );

        if (
          evaluator.type === "recommender" &&
          evaluator.memberId === meanUserId
        ) {
          evaluator.meanRecommenderThreshold = threshold;
        } else if (threshold != undefined) {
          throw new Error("Threshold specified for non-mean recommender");
        }

        const member = assertGetMember(draft, userId);
        const existingEvaluator = getEvaluator(draft, evaluatorId);
        if (existingEvaluator) {
          throw new Error(`Evaluator with id ${evaluatorId} already exists`);
        }

        member.evaluators[evaluatorId] = evaluator;
        break;
      }
      case "ADD_TRANCHE": {
        const { evaluatorId, trancheIndex, trancheHeight, trancheAllocations } =
          action;

        const evaluator = assertGetEvaluator(draft, evaluatorId);
        const newTranche: Tranche = {
          height: trancheHeight,
          allocations: trancheAllocations ?? {},
        };

        evaluator.trancheList.splice(trancheIndex, 0, newTranche);
        break;
      }
      case "ADD_PROPOSAL": {
        const {
          proposalId,
          entityType,
          proposalLongname,
          proposalFirstSubmittedDatetime,
          proposalShortname,
          proposalExternalId,
          proposalFormId,
          matchRate,
          targetOutsideDonations,
          baselineStandardGrantAmount,
          targetsFreedom,
          targetsFairness,
          longformLink,
        } = action;

        const proposalForm = assertGetProposalForm(draft, proposalFormId);

        if (proposalId in proposalForm.proposals) {
          console.warn(
            `ADD_PROPOSAL: Proposal with id ${proposalId} already exists`,
          );
          break;
          // throw new Error(`Proposal with id ${proposalId} already exists`);
        }

        const typedFields: TypedFields = {
          matchRate,
          baselineStandardGrantAmount,
          targetOutsideDonations,
          baseRequestAmount: 0,
          receivingOrgName: "",
          receivingOrgTaxId: "",
          receivingOrgLocation: "",
          sffApplicationId: "",
          targetsFreedom: targetsFreedom ?? false,
          targetsFairness: targetsFairness ?? false,
          longformLink: longformLink ?? "",
        };

        proposalForm.proposals[proposalId] = {
          id: proposalId,
          type: "proposal",
          entityType,
          shortname: proposalShortname,
          externalId: proposalExternalId,
          longname: proposalLongname ?? proposalShortname,
          firstSubmittedDatetime: proposalFirstSubmittedDatetime,
          lastSubmittedDatetime: proposalFirstSubmittedDatetime,
          status: "active",
          requestsMatchingPledge: false,
          fieldResponses: {},
          typedFields,
        };
        break;
      }
      case "START_EVALUATION": {
        const { evaluableId, evaluatorId, mvf, expectedMatchingFraction } =
          action;

        const evaluable = assertGetEvaluable(draft, evaluableId);
        if (
          evaluable.status === "removed" ||
          evaluable.status === "withdrawn"
        ) {
          throw new Error("Evaluable is unavailable");
        }

        const recusal = getRecusalForEvaluator(draft, evaluatorId, evaluableId);
        if (recusal) {
          throw new Error("Evaluator is recused");
        }

        const evaluator = assertGetEvaluator(draft, evaluatorId);
        const roundMember = getRoundMemberForEvaluator(draft, evaluatorId);

        if (!roundMember) {
          throw new Error("Round member not found");
        }

        const existingEvaluation = getEvaluation(
          draft,
          evaluatorId,
          evaluableId,
        );
        if (existingEvaluation) {
          const evaluation = evaluator.evaluations[evaluableId];

          if (
            evaluation.state === "deferred" ||
            evaluation.state === "recused"
          ) {
            throw new Error(
              "Evaluations can't be moved to active from deferred or recused",
            );
          } else if (evaluation.state === "evaluated") {
            console.warn("Evaluation is already active");
          }
        }

        if (evaluableId === evaluatorId) {
          throw new Error("Evaluator cannot evaluate themselves");
        }

        const evaluation: Evaluation = {
          evaluableId: evaluableId,
          evaluableType: evaluable.type,
          state: "evaluated",
          setToZero: false,
          expectedMatchingFraction: expectedMatchingFraction ?? 0.75,
        };

        let newMvf: MVF = mvf ?? [
          { dollar: 0, value: 0.5, placedManually: true },
          { dollar: 100000, value: 0.5, placedManually: true },
          { dollar: 100000, value: 0, placedManually: true },
        ];

        const validationError = validatePoints(newMvf, false);
        if (validationError) {
          newMvf = [
            { dollar: 0, value: 0.5, placedManually: true },
            { dollar: 100000, value: 0.5, placedManually: true },
            { dollar: 100000, value: 0, placedManually: true },
          ];
        }

        evaluator.evaluations[evaluableId] = evaluation;

        const mvfs = mvfProfileFromTrancheProfile(evaluator.trancheList);
        mvfs[evaluableId] = newMvf;
        evaluator.trancheList = tranchesFromMVFProfile(mvfs);
        break;
      }
      case "REMOVE_ROUND":
        draft.status = "deleted";
        break;
      case "REMOVE_ROUND_MEMBER": {
        const { userId } = action;
        const member = assertGetMember(draft, userId);
        member.status = "removed";
        break;
      }
      case "REMOVE_PROPOSAL_FORM": {
        const { proposalFormId } = action;
        const proposalForm = assertGetProposalForm(draft, proposalFormId);
        proposalForm.status = "deleted";
        break;
      }
      case "REMOVE_PROPOSAL_FORM_FIELD": {
        const { fieldId, proposalFormId } = action;
        const proposalForm = assertGetProposalForm(draft, proposalFormId);
        const fieldIndex = proposalForm.fields.findIndex(
          (f) => f.id === fieldId,
        );
        if (fieldIndex === -1) {
          const availableFields = proposalForm.fields.map((f) => f.id);
          throw new Error(
            `Field with id ${fieldId} not found (available fields: ${availableFields.join(", ")})`,
          );
        }
        proposalForm.fields[fieldIndex].status = "deleted";
        break;
      }
      case "REMOVE_EVALUATOR": {
        const { evaluatorId } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        evaluator.status = "removed";
        break;
      }
      case "REMOVE_TRANCHE": {
        const { evaluatorId, trancheIndex } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        // TODO: Should this be a soft delete? Case: user A deletes a tranche user B
        // deletes an evaluable, user A undoes the delete tranche event. Now user A
        // has a tranche that has a reference to an evaluable that no longer exists.

        // Soft deleted tranches are probably not going to work with interconverting
        // with MVFs. A possible solution is to do hard deletes and ensure that a
        // restored tranche has no invalid state, however we don't currently have a
        // way of doing that (maybe pass a snapshot of the current state to the
        // invert function?)

        // aside: and so dies the purity of the undo system
        evaluator.trancheList.splice(trancheIndex, 1);
        break;
      }
      case "REMOVE_PROPOSAL": {
        const { proposalId, proposalFormId } = action;
        const proposalForm = assertGetProposalForm(draft, proposalFormId);
        const proposal = assertGetProposal(proposalForm, proposalId);
        proposal.status = "withdrawn";
        break;
      }
      case "ROUND_NAME_CHANGE":
        draft.name = action.newName;
        break;
      case "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY": {
        const { memberId } = action;
        const member = assertGetMember(draft, memberId);
        if (action.track != null) member.track = action.track;
        if (action.initials != null) member.initials = action.initials;
        if (action.admin != null) member.isRoundAdmin = action.admin;
        break;
      }
      case "ROUND_MEMBER_PROPERTY_CHANGE": {
        const { memberId } = action;
        const member = assertGetMember(draft, memberId);
        if (action.feedbackVisibleByDefault != null) {
          member.feedbackVisibleByDefault = action.feedbackVisibleByDefault;
        }
        break;
      }
      case "EVALUATOR_PROPERTY_CHANGE": {
        const { evaluatorId, property, value } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        if (property === "shortname") {
          evaluator.shortname = value;
        }
        break;
      }
      case "FUNDER_BUDGET_CHANGE": {
        const { funderId, budget } = action;
        const funder = assertGetFunder(draft, funderId);
        funder.budget = budget;
        break;
      }
      case "FUNDER_SETTLEMENT_BUDGET_CHANGE": {
        const { funderId, budget } = action;
        const funder = assertGetFunder(draft, funderId);

        funder.speculationGrantSettlementBudget = budget;
        break;
      }
      case "FUNDER_SCORES_CHANGE": {
        const { funderId, scores } = action;
        const funder = assertGetFunder(draft, funderId);

        for (const scoreRow of scores) {
          const { recommenderId, proposalId, score } = scoreRow;

          if (!funder.scores[recommenderId]) {
            funder.scores[recommenderId] = {};
          }

          funder.scores[recommenderId][proposalId] = score;
        }
        break;
      }
      case "INCREMENTAL_EVALS_PUSH": {
        const { evaluatorId, evaluableId, incrementAmount } = action;

        const funder = assertGetFunder(draft, evaluatorId);

        // if there are some steps which have been undone, remove the undone steps
        if (funder.incrementalEvalSteps.length > funder.incrementalEvalIndex) {
          funder.incrementalEvalSteps = funder.incrementalEvalSteps.slice(
            0,
            funder.incrementalEvalIndex,
          );
        }

        funder.incrementalEvalSteps.push({
          evaluableId,
          incrementAmount,
        });
        funder.incrementalEvalIndex += 1;

        funder.trancheList = trancheListFromIncrementalEvalSteps(
          funder,
          draft.holdProposalId,
        );

        funder.budget = getFunderBudgetFromIncrementalEvalSteps(
          funder.incrementalEvalSteps,
          funder.incrementalEvalIndex,
          draft.holdProposalId,
        );
        break;
      }
      case "INCREMENTAL_EVALS_UNDO": {
        const { evaluatorId } = action;

        const funder = assertGetFunder(draft, evaluatorId);
        if (funder.incrementalEvalIndex === 0) {
          throw new Error("Nothing left to undo");
        }

        funder.incrementalEvalIndex -= 1;
        funder.trancheList = trancheListFromIncrementalEvalSteps(
          funder,
          draft.holdProposalId,
        );

        funder.budget = getFunderBudgetFromIncrementalEvalSteps(
          funder.incrementalEvalSteps,
          funder.incrementalEvalIndex,
          draft.holdProposalId,
        );
        break;
      }
      case "INCREMENTAL_EVALS_REDO": {
        const { evaluatorId } = action;

        const funder = assertGetFunder(draft, evaluatorId);
        if (funder.incrementalEvalIndex >= funder.incrementalEvalSteps.length) {
          throw new Error("Nothing left to redo");
        }

        funder.incrementalEvalIndex += 1;
        funder.trancheList = trancheListFromIncrementalEvalSteps(
          funder,
          draft.holdProposalId,
        );

        funder.budget = getFunderBudgetFromIncrementalEvalSteps(
          funder.incrementalEvalSteps,
          funder.incrementalEvalIndex,
          draft.holdProposalId,
        );
        break;
      }
      case "EVALUATION_FEEDBACK_CHANGE": {
        const { memberId, evaluableId, positive, negative } = action;
        const member = draft.members[memberId];
        if (!member) {
          throw new Error("Member not found");
        }
        if (!(evaluableId in member.evaluableFeedback)) {
          member.evaluableFeedback[evaluableId] = {
            positiveNegative: {
              type: "positiveNegative",
              positive: "positive",
              negative: "negative",
            },
          };
        }

        if (positive)
          member.evaluableFeedback[evaluableId].positiveNegative.positive =
            positive;
        if (negative)
          member.evaluableFeedback[evaluableId].positiveNegative.negative =
            negative;
        break;
      }
      case "EVALUATION_NOTES_CHANGE": {
        const { memberId, evaluableId, notes } = action;
        const member = draft.members[memberId];
        if (!member) {
          console.warn(
            `User ${memberId} not found, valid user ids:`,
            Object.keys(draft.members),
          );
          throw new Error("User not found");
        }
        member.evaluableNotes[evaluableId] = notes;
        break;
      }
      case "NOTES_AND_FEEDBACK_VISIBILITY_CHANGE": {
        const { memberId, evaluableId, visibility } = action;
        const member = draft.members[memberId];
        if (!member) {
          throw new Error("Member not found");
        }
        member.evaluablePermissions[evaluableId] = visibility;
        break;
      }
      case "EVALUATION_MVF_CHANGE": {
        const { evaluatorId, evaluableId, mvf, setZero } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        const validationError = validatePoints(mvf, false);
        if (validationError || !mvf) {
          return rawReturn(round);
        }

        const mvfs = mvfProfileFromTrancheProfile(evaluator.trancheList);
        mvfs[evaluableId] = mvf;
        evaluator.trancheList = tranchesFromMVFProfile(mvfs);

        const evaluation = evaluator.evaluations[evaluableId];
        if (evaluation) {
          evaluation.setToZero = Boolean(setZero);
        }
        break;
      }
      case "EVALUATION_SET_ZERO": {
        const { evaluatorId, evaluableId } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        const evaluable = assertGetEvaluable(draft, evaluableId);

        if (
          evaluable.status === "removed" ||
          evaluable.status === "withdrawn"
        ) {
          throw new Error("Evaluable is unavailable");
        }

        const mvfs = mvfProfileFromTrancheProfile(evaluator.trancheList);
        mvfs[evaluableId] = [
          { dollar: 0, value: 0, placedManually: true },
          { dollar: 100000, value: 0, placedManually: true },
        ];
        evaluator.trancheList = tranchesFromMVFProfile(mvfs);

        let evaluation = evaluator.evaluations[evaluableId];
        if (evaluation) {
          if (["deferred", "recused"].includes(evaluation.state)) {
            throw new Error(
              "Evaluation is deferred or recused, so can't be set to 0.",
            );
          }
          evaluation.state = "evaluated";
          evaluation.setToZero = true;
        } else {
          evaluator.evaluations[evaluableId] = {
            state: "evaluated",
            evaluableId,
            evaluableType: evaluable.type,
            setToZero: true,
          };
        }
        break;
      }
      case "SET_EXPECTED_MATCHING_FRACTION": {
        const { evaluatorId, evaluableId, expectedMatchingFraction } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        const evaluation = evaluator.evaluations[evaluableId];
        evaluation.expectedMatchingFraction = expectedMatchingFraction;
        break;
      }
      case "EVALUATION_STATUS_CHANGE": {
        const { evaluatorId, evaluableId } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        const evaluable = assertGetEvaluable(draft, evaluableId);
        const member = getMemberForEvaluator(draft, evaluatorId);

        if (
          evaluable.status === "removed" ||
          evaluable.status === "withdrawn"
        ) {
          throw new Error("Evaluable is unavailable");
        }

        if (!member) {
          throw new Error(`Member not found for evaluator ${evaluatorId}`);
        }

        let evaluation = evaluator.evaluations[evaluableId];
        if (!evaluation) {
          evaluation = {
            state: "not-started",
            evaluableId,
            evaluableType: evaluable.type,
            setToZero: false,
          };
          evaluator.evaluations[evaluableId] = evaluation;
        }

        const possibleDisclosure =
          draft.proposalDisclosure[`${evaluableId}%${member.id}`];
        const isRecused = possibleDisclosure?.decision === "recused";

        const throwIfNotLegacy = (msg: string) => {
          if (!action.legacy) {
            throw new Error(msg);
          }
        };

        if ((action.change === "defer") === (action.deferredTo == undefined)) {
          throw new Error("deferredTo must be set iff change === 'defer'");
        }

        if (action.change === "start") {
          if (
            !["not-started", "hidden", "canceled"].includes(evaluation.state)
          ) {
            throwIfNotLegacy(
              "Evaluation must be in the not-started, hidden, or canceled state to start",
            );
          }
          evaluation.state = "evaluated";
        } else if (action.change === "cancel") {
          if (evaluation.state !== "evaluated") {
            throwIfNotLegacy(
              "Evaluation must be in the evaluated state to cancel",
            );
          }
          if (isRecused) {
            throwIfNotLegacy("Can't cancel a recused evaluation");
          }
          evaluation.state = "canceled";
        } else if (action.change === "hide") {
          if (!["not-started", "canceled"].includes(evaluation.state)) {
            throwIfNotLegacy(
              "Evaluation must be in the not-started or canceled state to hide",
            );
          }
          if (isRecused) {
            throwIfNotLegacy("Can't hide a recused evaluation");
          }
          evaluation.state = "hidden";
        } else if (action.change === "unhide") {
          if (evaluation.state !== "hidden") {
            throwIfNotLegacy(
              "Evaluation must be in the hidden state to unhide",
            );
          }
          if (isRecused) {
            throwIfNotLegacy("Can't unhide a recused evaluation");
          }
          evaluation.state = "canceled";
        } else if (action.change === "recuse") {
          if (evaluation.state !== "deferred") {
            throwIfNotLegacy(
              "Evaluation must be in the deferred state to recuse",
            );
          }
          if (!isRecused) {
            throwIfNotLegacy(
              "Can't recuse an evaluation if you're not recused",
            );
          }
          evaluation.state = "recused";
          evaluation.deferredTo = undefined;
        } else if (action.change === "defer") {
          if (evaluation.state !== "recused") {
            throwIfNotLegacy(
              "Evaluation must be in the recused state to defer",
            );
          }
          if (!isRecused) {
            throwIfNotLegacy("Can't defer an evaluation if you're not recused");
          }
          evaluation.state = "deferred";
          evaluation.deferredTo = action.deferredTo;
        }
        break;
      }
      case "NORMALIZE_EVALUATIONS": {
        const { evaluatorId } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        evaluator.trancheList = normalizeTrancheList(evaluator.trancheList);
        break;
      }
      case "SET_TRANCHE_ALLOCATION": {
        const { evaluatorId, trancheIndex, amount, evaluableId } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        const tranche = evaluator.trancheList[trancheIndex];
        tranche.allocations[evaluableId] = {
          amount,
          bottomPlaced: true,
        };
        break;
      }
      case "SET_TRANCHE_HEIGHT": {
        const { evaluatorId, trancheIndex, height } = action;
        const evaluator = assertGetEvaluator(draft, evaluatorId);
        const tranche = evaluator.trancheList[trancheIndex];
        tranche.height = height;
        break;
      }
      case "PROPOSAL_FORM_FIELD_CHANGE": {
        const { proposalFormId, field } = action;
        const proposalForm = assertGetProposalForm(draft, proposalFormId);
        const fieldIndex = proposalForm.fields.findIndex(
          (f) => f.id === field.id,
        );
        if (fieldIndex === -1) {
          throw new Error(`Field with id ${field.id} not found`);
        }
        proposalForm.fields[fieldIndex] = field;
        break;
      }
      case "PROPOSAL_LONGNAME_CHANGE": {
        const { proposalId, proposalFormId, longname } = action;
        const proposalForm = assertGetProposalForm(draft, proposalFormId);
        const proposal = assertGetProposal(proposalForm, proposalId);
        proposal.longname = longname;
        break;
      }
      case "PROPOSAL_SHORTNAME_CHANGE": {
        const { proposalId, shortname } = action;
        const proposal = getProposal(draft, proposalId);
        if (!proposal) {
          throw new Error("Failed to find proposal");
        }
        proposal.shortname = shortname;
        break;
      }
      case "PROPOSAL_FIELD_RESPONSE_CHANGE": {
        const { proposalId, proposalFormId, fieldId, response } = action;
        const proposalForm = assertGetProposalForm(draft, proposalFormId);
        const proposal = assertGetProposal(proposalForm, proposalId);
        proposal.fieldResponses[fieldId] = { fieldId, ...response };
        break;
      }
      case "PROPOSAL_FIELD_TITLE_CHANGE": {
        const { proposalFormId, fieldId, fieldTitle } = action;
        const proposalForm = assertGetProposalForm(draft, proposalFormId);
        const field = assertGetField(proposalForm, fieldId);
        field.title = fieldTitle;
        break;
      }
      case "PROPOSAL_FIELD_DESCRIPTION_CHANGE": {
        const { proposalFormId, fieldId, fieldDescription } = action;
        const proposalForm = assertGetProposalForm(draft, proposalFormId);
        const field = assertGetField(proposalForm, fieldId);
        field.description = fieldDescription;
        break;
      }
      case "PROPOSAL_FORM_EXTERNAL_SYNC": {
        const proposalForm = assertGetProposalForm(
          draft,
          action.proposalFormId,
        );
        proposalForm.proposals = action.proposals;

        for (const field of action.fields) {
          const fieldIndex = proposalForm.fields.findIndex(
            (f) => f.id === field.id,
          );
          if (fieldIndex === -1) {
            proposalForm.fields.push(field);
          } else {
            proposalForm.fields[fieldIndex] = field;
          }
        }
        break;
      }
      case "SET_TRACK_SETTINGS": {
        const { track, status, evaluationsDueDate } = action;
        draft.trackSettings[track].status = status;
        if (evaluationsDueDate) {
          draft.trackSettings[track].evaluationsDueDate = evaluationsDueDate;
        }
        break;
      }
      case "INVESTIGATION_INTEREST_CHANGE": {
        const { proposalId, memberId, interest, confirmedReviewer } = action;
        const key = `${proposalId}%${memberId}`;

        if (interest != null && (interest < 0 || interest > 4)) {
          throw new Error("Interest must be between 0 and 4 (inclusive)");
        }

        if (!draft.investigation[key]) {
          draft.investigation[key] = {
            interest: undefined,
            confirmedReviewer: false,
          };
        }
        if (interest !== undefined) {
          // If interest is explicitly null, set it to undefined, otherwise set it to the number.
          draft.investigation[key].interest =
            interest === null ? undefined : interest;
        }
        if (confirmedReviewer !== undefined) {
          draft.investigation[key].confirmedReviewer = confirmedReviewer;
        }
        break;
      }
      case "REVIEW_PROGRESS_CHANGE": {
        const { proposalId, memberId, progress } = action;
        if (progress != null && (progress < 1 || progress > 4)) {
          throw new Error("Progress must be between 1 and 4 (inclusive)");
        }
        const proposal = getProposal(draft, proposalId);
        if (!proposal) {
          throw new Error("Proposal not found");
        }
        if (progress === null) {
          delete draft.reviewProgress[`${proposalId}%${memberId}`];
        } else {
          draft.reviewProgress[`${proposalId}%${memberId}`] = progress;
        }
        break;
      }
      case "PROPOSAL_DISCLOSURE_CHANGE": {
        const { proposalId, userId, hasDisclosure, selfRecused, disclosure } =
          action;

        const key = `${proposalId}%${userId}`;
        if (!draft.proposalDisclosure[key]) {
          draft.proposalDisclosure[key] = {
            hasDisclosure,
            selfRecused,
            disclosure,
            votes: {},
            decision: null,
          };
        }

        const proposalDisclosure = draft.proposalDisclosure[key];
        proposalDisclosure.hasDisclosure = hasDisclosure;
        proposalDisclosure.selfRecused = selfRecused;
        proposalDisclosure.disclosure = disclosure;

        const decision = recusalDecision(
          recusalVoters(
            Object.values(draft.members),
            draft.members[userId],
            draft.members[userId].track,
          ),
          proposalDisclosure,
        );
        proposalDisclosure.decision = decision;

        possiblyUpdateEvaluationBasedOnRecusalVote(
          draft.members[userId],
          proposalId,
          decision,
        );
        break;
      }
      case "RECUSAL_VOTE": {
        const { proposalId, votingUserId, subjectUserId, vote } = action;
        const disclosure =
          draft.proposalDisclosure[`${proposalId}%${subjectUserId}`];
        if (!disclosure || !disclosure.hasDisclosure) {
          if (!action.legacy) {
            throw new Error("Can't vote on a non-disclosure");
          }
        }
        disclosure.votes[votingUserId] = vote;

        const votingMember = assertGetMember(draft, votingUserId);
        const voterTrack = votingMember.track;
        if (voterTrack === "NO_TRACK") {
          throw new Error("User must be in a track to vote on recusals");
        }

        const evaluatorMember = assertGetMember(draft, subjectUserId);
        const decision = recusalDecision(
          recusalVoters(
            Object.values(draft.members),
            evaluatorMember,
            voterTrack,
          ),
          disclosure,
        );
        disclosure.decision = decision;

        possiblyUpdateEvaluationBasedOnRecusalVote(
          draft.members[subjectUserId],
          proposalId,
          decision,
        );

        break;
      }
      case "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE": {
        const { evaluatorId, outsideDollarSubstitutionRate } = action;
        const recommender = assertGetRecommender(draft, evaluatorId);
        recommender.outsideDollarSubstitutionRate =
          outsideDollarSubstitutionRate;
        break;
      }
      case "ONE_TIME_ADMIN_ACTION_1": {
        const sffRollingApplicationFormId = "form-rOayziKPN_";
        const affectedProposals: {
          badCurrentProposalId: string;
          goodOriginalProposalId: string;
        }[] = [
          {
            // Effective Institutions Project
            badCurrentProposalId: "p-kn4gwcC36A",
            goodOriginalProposalId: "p-eRe3M_KEnO",
          },
          {
            // Ulyssean PBC
            badCurrentProposalId: "p-TYDrnl-15Q",
            goodOriginalProposalId: "p-X0DnZuWpDC",
          },
          {
            // Halcyon Futures Inc
            badCurrentProposalId: "p-aTcx5GR6hB",
            goodOriginalProposalId: "p-pwbiljchsd",
          },
          {
            // FAR AI
            badCurrentProposalId: "p-mGLGrFWMcZ",
            goodOriginalProposalId: "p-18ZD6FWNtk",
          },
        ];

        const proposalForm = assertGetProposalForm(
          draft,
          sffRollingApplicationFormId,
        );
        for (const {
          badCurrentProposalId,
          goodOriginalProposalId,
        } of affectedProposals) {
          const proposal = assertGetProposal(
            proposalForm,
            badCurrentProposalId,
          );
          proposal.id = goodOriginalProposalId;
          delete proposalForm.proposals[badCurrentProposalId];
          proposalForm.proposals[goodOriginalProposalId] = proposal;
        }
        break;
      }
      case "ONE_TIME_ADMIN_ACTION_2": {
        const evaluator = assertGetEvaluator(draft, "rec-XhOqjaff");
        const idsToRemove = [
          "p-kn4gwcC36A",
          "p-TYDrnl-15Q",
          "p-aTcx5GR6hB",
          "p-mGLGrFWMcZ",
        ];
        delete evaluator.evaluations["p-kn4gwcC36A"];
        for (const id of idsToRemove) {
          delete evaluator.evaluations[id];
        }
        for (let i = 0; i < evaluator.trancheList.length; i++) {
          const allocations = evaluator.trancheList[i].allocations;
          for (const id of idsToRemove) {
            delete allocations[id];
          }
        }
        break;
      }
      default:
        assertUnreachable(action);
    }
  });
}

function initializeRoundAggregate(
  action: ActionMessage,
  payload: AddRound,
): RoundAggregate {
  const { version, aggregateId, timestamp } = action;
  const {
    name,
    userId,
    holdProposalId,
    holdProposalName,
    defaultProposalFormId,
    initials,
    track,
  } = payload;

  const funderId = payload.initialFunderId;
  const recommenderId = payload.initialRecommenderId;

  let funderNode: Evaluator | null = funderId
    ? {
        ...initFunder(funderId, userId, `${initials} Funder`, holdProposalId),
        type: "funder",
        budget: 1_000_000,
      }
    : null;
  if (funderNode && recommenderId) {
    funderNode = {
      ...funderNode,
      evaluations: {
        ...funderNode.evaluations,
        [recommenderId]: {
          evaluableId: recommenderId,
          evaluableType: "recommender",
          state: "not-started",
        },
      },
      trancheList: [
        {
          height: 1.0 - funderNode.trancheList[0].height,
          allocations: {
            [recommenderId]: {
              amount: 1_000_000,
              bottomPlaced: true,
            },
          },
        },
        ...funderNode.trancheList, // inherit the hold tranche
      ],
    };
  }

  return {
    type: "Round",
    status: "active",
    trackSettings: {
      MAIN: {
        status: "1.onboarding",
        evaluationsDueDate: undefined,
      },
      FAIRNESS: {
        status: "1.onboarding",
        evaluationsDueDate: undefined,
      },
      FREEDOM: {
        status: "1.onboarding",
        evaluationsDueDate: undefined,
      },
    },
    id: aggregateId,
    version,
    name,
    holdProposalId: holdProposalId,
    members: {
      [userId]: {
        createdAt: timestamp,
        status: "active",
        id: userId,
        userId,
        isRoundAdmin: true,
        feedbackVisibleByDefault: true,
        track,
        initials,
        evaluableFeedback: {},
        evaluableNotes: {},
        evaluablePermissions: {},
        evaluators: {
          ...(funderNode ? { [funderId!]: funderNode } : {}),
          ...(recommenderId
            ? {
                [recommenderId]: initRecommender(
                  recommenderId,
                  userId,
                  `${initials} Recommender`,
                  holdProposalId,
                ),
              }
            : {}),
        },
      },
      [meanUserId]: {
        status: "active",
        createdAt: timestamp,
        id: meanUserId,
        userId: meanUserId,
        isRoundAdmin: false,
        track,
        initials: "Mean",
        feedbackVisibleByDefault: true,
        evaluableFeedback: {},
        evaluableNotes: {},
        evaluablePermissions: {},
        evaluators: {},
      },
    },
    proposalForms: {
      [defaultProposalFormId]: {
        id: defaultProposalFormId,
        status: "active",
        name: `${name} Default Form`,
        googleFormId: "",
        spreadsheetId: "",
        spreadsheetRange: "",
        proposals: {
          [holdProposalId]: {
            type: "proposal",
            status: "active",
            id: holdProposalId,
            shortname: holdProposalName,
            entityType: "non-profit",
            fieldResponses: {},
            firstSubmittedDatetime: timestamp,
            longname: holdProposalName,
            lastSubmittedDatetime: timestamp,
            requestsMatchingPledge: false,
            typedFields: {
              baseRequestAmount: 0,
              receivingOrgName: "",
              receivingOrgTaxId: "",
              receivingOrgLocation: "",
              sffApplicationId: "",
              targetsFreedom: false,
              targetsFairness: false,
              longformLink: "http://example.com",
            },
          },
        },
        fields: [],
      },
    },
    investigation: {},
    reviewProgress: {},
    proposalDisclosure: {},
  };
}

export type ReducerResult = RoundAggregate | Error;

export class Reducer {
  performAction(
    agg: RoundAggregate | null,
    someActionMessage: ActionMessage | UnconfirmedActionMessage,
  ): ReducerResult {
    let unappliedAction: ActionMessage;
    if (
      !("version" in someActionMessage) ||
      someActionMessage.version == null ||
      !("timestamp" in someActionMessage)
    ) {
      // This is an UnappliedUnconfirmedActionMessage which already has: aggregateId, payload, userId, id
      unappliedAction = {
        ...someActionMessage,
        version: agg ? agg.version + 1 : 0,
        timestamp: new Date().toISOString(),
      };
    } else {
      // An ActionMessage is already an UnappliedActionMessage (plus patches and inversePatches)
      unappliedAction = someActionMessage;
    }

    if (agg == null) {
      const action_ = unappliedAction.payload;
      if (action_.type === "CREATE_ROUND") {
        return initializeRoundAggregate(unappliedAction, action_);
      } else {
        return new Error(
          `Aggregate ${unappliedAction.aggregateId} not found when applying ${unappliedAction.payload.type}`,
        );
      }
    }

    try {
      const message = isActionPermitted(agg, unappliedAction);
      if (message !== true) {
        return new Error(
          `Not permitted (${unappliedAction.payload.type}): ${message}`,
        );
      }
    } catch (e) {
      console.error(e);
      console.log("Failed to apply action", unappliedAction);
      return new ChainedError(
        `Failed to apply action ${unappliedAction.id} (${unappliedAction.payload.type})`,
        e as Error,
      );
    }

    try {
      const actionResult = applyAction(agg, unappliedAction);
      const newRound = actionResult === agg ? cloneDeep(agg) : actionResult;
      newRound.version = unappliedAction.version;
      return newRound;
    } catch (e) {
      console.error(e);
      console.log("Failed to apply action", unappliedAction);
      if (e instanceof Error) {
        return new ChainedError(
          `Failed to apply action ${unappliedAction.id}`,
          e,
        );
      } else {
        return new Error(
          `Failed to apply action ${unappliedAction.id} (unknown non-error exception)`,
        );
      }
    }
  }

  runActionsOnAggregate(
    actions: UnconfirmedActionMessage[],
    agg: RoundAggregate | null = null,
  ): RoundAggregate | Error {
    for (const actionMessage of actions) {
      const result = this.performAction(agg, actionMessage);
      if (result instanceof Error) {
        return result;
      }
      agg = result;
    }

    if (agg == null) {
      return new Error("Didn't reduce any actions");
    }

    return agg;
  }
}

export const roundReducer = new Reducer();

export type RoundSchema = Action;

function recusalVoters(
  roundMembers: RoundMember[],
  voteSubject: RoundMember,
  voterTrack: Track,
) {
  const isInRelevantTrack =
    voteSubject.track === "NO_TRACK"
      ? (m: RoundMember) => m.track !== "NO_TRACK"
      : (m: RoundMember) => m.track === voterTrack;

  const relevantMembers = roundMembers.filter(
    (m) =>
      m.id !== voteSubject.id &&
      isInRelevantTrack(m) &&
      m.status === "active" &&
      Object.values(m.evaluators).some(
        (e) => e.type === "recommender" && !evaluatorIsMeanRecommender(e),
      ),
  );

  return relevantMembers;
}

function recusalDecision(voters: RoundMember[], disclosure: Disclosure) {
  let yesCount = 0;
  let noCount = 0;
  let abstainCount = 0;
  for (const member of voters) {
    const memberVote = disclosure.votes[member.id];
    if (memberVote === "y") yesCount++;
    else if (memberVote === "n") noCount++;
    else if (memberVote === "a") abstainCount++;
  }

  // Say there were 4 voting members and 2 abstainees. Then we need to add
  // epsilon (0.1) to the numerator to avoid a quorum of 1.
  const quorum = Math.ceil((voters.length - abstainCount + 0.1) / 2);
  const decision =
    disclosure.selfRecused || yesCount >= quorum
      ? "recused"
      : noCount >= quorum
        ? "not-recused"
        : null;

  return decision;
}

function possiblyUpdateEvaluationBasedOnRecusalVote(
  member: RoundMember,
  proposalId: string,
  decision: "recused" | "not-recused" | null,
) {
  for (const evaluator of Object.values(member.evaluators)) {
    if (evaluator.type === "recommender") {
      const evaluation = evaluator.evaluations[proposalId];
      const currentState = evaluation?.state;

      if (decision === "recused") {
        if (evaluation == null) {
          evaluator.evaluations[proposalId] = {
            evaluableId: proposalId,
            evaluableType: "proposal",
            state: "recused",
            deferredTo: undefined,
          };
        } else if (
          ["not-started", "evaluated", "canceled", "hidden"].includes(
            currentState,
          )
        ) {
          evaluation.state = evaluation.deferredTo ? "deferred" : "recused";
        }
      } else {
        if (evaluation && ["recused", "deferred"].includes(currentState)) {
          evaluation.state = "evaluated";
        }
      }
    }
  }
}
